import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DotsSixVertical } from '@phosphor-icons/react';
import { ContactRecord } from 'src/services/integration-app.service';
import { cn } from 'src/utils/className';

export interface CrmField {
  value: string;
  label: string;
  description?: string;
}

interface DraggableCrmFieldProps {
  field: CrmField;
  sampleContacts?: ContactRecord[];
}

export const DraggableCrmField: React.FC<DraggableCrmFieldProps> = ({
  field,
  sampleContacts = [],
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: field.value,
    data: { field },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'transform 200ms ease',
  };

  const getSampleValues = (): string[] => {
    if (sampleContacts.length === 0) return [];

    return sampleContacts
      .slice(0, 3)
      .map((contact) => {
        const fieldValue =
          contact.fields[field.value as keyof typeof contact.fields];
        return fieldValue ? String(fieldValue) : '';
      })
      .filter((value) => value.trim() !== '');
  };

  const sampleValues = getSampleValues();
  const hasSampleValues = sampleValues.length > 0;

  if (isDragging) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className="h-[52px] bg-neutral-200/50 border border-dashed border-primary/30 rounded-lg opacity-50"
      />
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        'flex items-center gap-3 p-3 bg-neutral-0 border border-neutral-200 rounded-lg',
        'cursor-grab hover:bg-neutral-50 hover:border-primary-300 hover:shadow-sm transition-all',
        'active:cursor-grabbing',
      )}
    >
      <DotsSixVertical size={16} className="text-neutral-400 shrink-0" />
      <div className="flex flex-col min-w-0">
        <span className="font-medium text-sm text-neutral-700 truncate">
          {field.label}
        </span>
        {hasSampleValues && (
          <span className="text-[10px] text-neutral-500 truncate mt-0.5">
            {sampleValues.join(', ')}
          </span>
        )}
      </div>
    </div>
  );
};
